import * as validate from "./validate";
import {createPendingSaveBarrier, hasPendingSaves} from "./saveInFlightManager";

export const QUESTIONNAIRE_SAVE_INVALID = "invalid";
export const QUESTIONNAIRE_SAVE_DEPENDENCY_FAILED = "dependency-failed";
export const QUESTIONNAIRE_SAVE_CANCELLED = "cancelled";

const CANCELLED = Symbol("questionnaire-save-cancelled");

export function createQuestionnaireSaveCancellationToken() {
    let cancelled = false;
    const listeners = new Set();

    return {
        cancel() {
            if (cancelled) {
                return;
            }
            cancelled = true;
            const cancellationListeners = Array.from(listeners);
            listeners.clear();
            cancellationListeners.forEach(listener => listener());
        },
        isCancelled() {
            return cancelled;
        },
        onCancel(listener) {
            if (cancelled) {
                listener();
                return () => {};
            }
            listeners.add(listener);
            return () => listeners.delete(listener);
        }
    };
}

const awaitWithCancellation = (promise, cancellationToken) => {
    if (cancellationToken.isCancelled()) {
        return Promise.resolve(CANCELLED);
    }

    let unsubscribe = () => {};
    const cancellation = new Promise(resolve => {
        unsubscribe = cancellationToken.onCancel(() => resolve(CANCELLED));
    });
    return Promise.race([Promise.resolve(promise), cancellation])
        .finally(() => unsubscribe());
};

const getNameValidationError = (template, validationResults, cloneMode) => {
    return validate.templateNameAndQualifierUnique(
        {
            template,
            options: {validate: validationResults}
        },
        "Questionnaire Name",
        cloneMode
    );
};

const nameAndQualifierMatch = (template, validatedValues) => {
    return template.name === validatedValues.name
        && template.nameQualifier === validatedValues.nameQualifier;
};

export async function validateAndSaveQuestionnaire({
    flushPendingUpdates,
    getEditorState,
    validateNameAndQualifier,
    save,
    cloneMode = false,
    onInvalid,
    cancellationToken = createQuestionnaireSaveCancellationToken()
}) {
    const saveBarrier = createPendingSaveBarrier();
    const cancelledResult = () => ({status: QUESTIONNAIRE_SAVE_CANCELLED});
    const closeBarrierOnCancel = cancellationToken.onCancel(() => saveBarrier.close());
    try {
        validationLoop: while (true) {
            if (cancellationToken.isCancelled()) {
                return cancelledResult();
            }
            flushPendingUpdates();
            if (cancellationToken.isCancelled()) {
                return cancelledResult();
            }
            let editorState = getEditorState();
            const localError = getNameValidationError(editorState.template, [], cloneMode);

            if (localError !== " ") {
                if (cancellationToken.isCancelled()) {
                    return cancelledResult();
                }
                onInvalid?.(localError);
                return {status: QUESTIONNAIRE_SAVE_INVALID, error: localError};
            }

            const valuesToValidate = {
                name: editorState.template.name,
                nameQualifier: editorState.template.nameQualifier
            };
            const validationResult = await awaitWithCancellation(
                validateNameAndQualifier(
                    valuesToValidate.name,
                    valuesToValidate.nameQualifier
                ),
                cancellationToken
            );

            if (validationResult === CANCELLED || cancellationToken.isCancelled()) {
                return cancelledResult();
            }
            flushPendingUpdates();
            if (cancellationToken.isCancelled()) {
                return cancelledResult();
            }
            editorState = getEditorState();

            if (!nameAndQualifierMatch(editorState.template, valuesToValidate)) {
                if (cancellationToken.isCancelled()) {
                    return cancelledResult();
                }
                continue;
            }

            if (!validationResult?.succeeded) {
                return validationResult;
            }

            const validationError = getNameValidationError(
                editorState.template,
                validationResult.collection,
                cloneMode
            );
            if (validationError !== " ") {
                if (cancellationToken.isCancelled()) {
                    return cancelledResult();
                }
                onInvalid?.(validationError);
                return {status: QUESTIONNAIRE_SAVE_INVALID, error: validationError};
            }

            while (true) {
                if (cancellationToken.isCancelled()) {
                    return cancelledResult();
                }
                try {
                    const barrierResult = await awaitWithCancellation(
                        saveBarrier.wait(),
                        cancellationToken
                    );
                    if (barrierResult === CANCELLED || cancellationToken.isCancelled()) {
                        return cancelledResult();
                    }
                }
                catch (error) {
                    if (cancellationToken.isCancelled()) {
                        return cancelledResult();
                    }
                    return {
                        status: QUESTIONNAIRE_SAVE_DEPENDENCY_FAILED,
                        error
                    };
                }
                if (saveBarrier.hasUnobservedSaves() || hasPendingSaves()) {
                    continue;
                }

                if (cancellationToken.isCancelled()) {
                    return cancelledResult();
                }
                flushPendingUpdates();
                if (cancellationToken.isCancelled()) {
                    return cancelledResult();
                }
                const latestEditorState = getEditorState();
                if (saveBarrier.hasUnobservedSaves() || hasPendingSaves()) {
                    continue;
                }
                if (!nameAndQualifierMatch(latestEditorState.template, valuesToValidate)) {
                    if (cancellationToken.isCancelled()) {
                        return cancelledResult();
                    }
                    continue validationLoop;
                }

                if (cancellationToken.isCancelled()) {
                    return cancelledResult();
                }
                saveBarrier.close();
                return save(
                    latestEditorState.template,
                    {
                        ...latestEditorState.options,
                        validate: validationResult.collection
                    }
                );
            }
        }
    }
    finally {
        closeBarrierOnCancel();
        saveBarrier.close();
    }
}
